import api from '../../services/api';

export interface Pricing {
  freeSeats: number;
  pricePerSeatCents: number;
  pricePerSeatYuan: number;
  pricePerSeatUsdApprox: number;
  periodDays: number;
  graceDays: number;
  currency: string;
  methods: string[];
  agentsCountAsSeats: boolean;
}

/** 接口失败时的兜底，数值和后端 billing.rules.ts 保持一致；正常情况以接口返回为准 */
export const FALLBACK_PRICING: Pricing = {
  freeSeats: 3,
  pricePerSeatCents: 2900,
  pricePerSeatYuan: 29,
  pricePerSeatUsdApprox: 5,
  periodDays: 365,
  graceDays: 7,
  currency: 'CNY',
  methods: ['ALIPAY', 'WECHAT'],
  agentsCountAsSeats: false,
};

export type BillingState = 'FREE' | 'PAID' | 'GRACE' | 'READONLY';

export interface TeamBilling {
  state: BillingState;
  memberCount: number;
  freeSeats: number;
  activePaidSeats: number;
  capacity: number;
  paidUntil: string | null;
  paidActive: boolean;
  inPaidGrace: boolean;
  inLaunchGrace: boolean;
  graceEndsAt: string | null;
  writable: boolean;
  seatsNeeded: number;
  canManage: boolean;
  pricing: Pricing;
  /** 后端是模拟支付模式（只在开发/测试环境） */
  mock: boolean;
}

export type PayIntent = 'BUY_SEATS' | 'RENEW';
export type PayMethod = 'ALIPAY' | 'WECHAT';

export interface Quote {
  kind: 'NEW' | 'ADD' | 'RENEW';
  seats: number;
  amountCents: number;
  newPaidSeats: number;
  newPaidUntil: string;
  billedDays: number;
  description: string;
}

export interface CreatedOrder {
  orderId: string;
  method: PayMethod;
  qrCode: string;
  amountCents: number;
  expireAt: string;
  quote: Quote;
}

export interface OrderStatus {
  orderId: string;
  status: 'PENDING' | 'PAID' | 'CLOSED';
  method: PayMethod;
  amountCents: number;
  description: string;
  qrCode: string | null;
  expireAt: string;
  paidAt: string | null;
  kind: string;
  seats: number;
}

export interface PaidOrder {
  id: string;
  kind: string;
  seats: number;
  amountCents: number;
  method: PayMethod;
  paidAt: string;
  description: string;
}

export const billingService = {
  async getPricing(): Promise<Pricing> {
    return (await api.get('/billing/pricing')).data;
  },
  async getTeamBilling(teamId: string): Promise<TeamBilling> {
    return (await api.get(`/billing/teams/${teamId}`)).data;
  },
  async quote(teamId: string, intent: PayIntent, seats?: number): Promise<Quote> {
    return (await api.post(`/billing/teams/${teamId}/quote`, { intent, seats })).data;
  },
  async createOrder(teamId: string, intent: PayIntent, seats: number | undefined, method: PayMethod): Promise<CreatedOrder> {
    return (await api.post(`/billing/teams/${teamId}/orders`, { intent, seats, method })).data;
  },
  async getOrder(orderId: string): Promise<OrderStatus> {
    return (await api.get(`/billing/orders/${orderId}`)).data;
  },
  async mockPay(orderId: string): Promise<void> {
    await api.post(`/billing/orders/${orderId}/mock-pay`);
  },
  async listOrders(teamId: string): Promise<PaidOrder[]> {
    return (await api.get(`/billing/teams/${teamId}/orders`)).data;
  },
};

/** 金额（分）→ 「¥29」「¥58.50」 */
export function yuan(cents: number): string {
  const v = (cents / 100).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  return `¥${v}`;
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export function apiErrorMessage(e: any, fallback = '操作失败，稍后再试'): string {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join('；') : m || fallback;
}
